import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  Switch,
  FormControlLabel,
  Divider,
  Button,
  IconButton,
  AppBar,
  Toolbar,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  Notifications,
  Security,
  Language,
  ColorLens,
  ShoppingCart,
  AccountCircle,
  Settings as SettingsIcon,
  ExitToApp,
  Person,
  Help,
} from '@mui/icons-material';
import Footer from '../components/Footer';
import logo0 from '../assets/logo0.png';
import { Link } from 'react-router-dom';

const PageWrapper = styled(Box)({
  backgroundColor: '#f2f7ff',
  minHeight: '100vh',
  display: 'flex',
  flexDirection: 'column'
});

// New Header Styles
const StyledAppBar = styled(AppBar)({
  backgroundColor: '#000000',
  boxShadow: 'none',
  borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
});

const LogoImage = styled('img')({
  height: '30px',
  filter: 'brightness(0) invert(1)', // This makes the logo white
  cursor: 'pointer'
});

const HeaderIconButton = styled(IconButton)({
  color: '#ffffff',
  marginLeft: '16px',
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.1)'
  }
});

const SettingsCard = styled(Paper)({
  padding: '24px',
  borderRadius: '8px',
  border: '1px solid #e0e0e0',
  '&:hover': {
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
  }
});

const StyledSwitch = styled(Switch)({
  '& .MuiSwitch-switchBase.Mui-checked': {
    color: '#108a00',
    '&:hover': {
      backgroundColor: 'rgba(16, 138, 0, 0.04)',
    },
  },
  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
    backgroundColor: '#108a00',
  },
});

const StyledMenu = styled(Menu)(({ theme }) => ({
  '& .MuiPaper-root': {
    borderRadius: 6,
    marginTop: theme.spacing(1),
    minWidth: 180,
    boxShadow: 'rgb(255, 255, 255) 0px 0px 0px 0px, rgba(0, 0, 0, 0.05) 0px 0px 0px 1px, rgba(0, 0, 0, 0.1) 0px 10px 15px -3px, rgba(0, 0, 0, 0.05) 0px 4px 6px -2px',
  }
}));

const Settings = () => {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleProfileClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      {/* Header with Profile Dropdown */}
      <StyledAppBar position="static">
        <Toolbar>
          <Container maxWidth="lg" sx={{ 
            display: 'flex', 
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <Link to="/dashboard">
              <LogoImage src={logo0} alt="Logo" />
            </Link>
            <Box>
              <HeaderIconButton>
                <ShoppingCart />
              </HeaderIconButton>
              <HeaderIconButton
                onClick={handleProfileClick}
                aria-controls={open ? 'profile-menu' : undefined}
                aria-haspopup="true"
                aria-expanded={open ? 'true' : undefined}
              >
                <AccountCircle />
              </HeaderIconButton>
            </Box>
          </Container>
        </Toolbar>
      </StyledAppBar>

      {/* Profile Dropdown Menu */}
      <StyledMenu
        id="profile-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        onClick={handleClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem component={Link} to="/profile">
          <ListItemIcon>
            <Person fontSize="small" />
          </ListItemIcon>
          <ListItemText>Profile</ListItemText>
        </MenuItem>
        
        <MenuItem component={Link} to="/settings">
          <ListItemIcon>
            <SettingsIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Settings</ListItemText>
        </MenuItem>

        <MenuItem component={Link} to="/help">
          <ListItemIcon>
            <Help fontSize="small" />
          </ListItemIcon>
          <ListItemText>Help Center</ListItemText>
        </MenuItem>

        <Divider />
        
        <MenuItem onClick={() => { /* Add logout logic */ }}>
          <ListItemIcon>
            <ExitToApp fontSize="small" />
          </ListItemIcon>
          <ListItemText>Logout</ListItemText>
        </MenuItem>
      </StyledMenu>

      <PageWrapper>
        <Container maxWidth="lg" sx={{ py: 4, flex: 1 }}>
          {/* Header Section */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h4" sx={{ 
              color: '#108a00',
              fontWeight: 600,
              mb: 1
            }}>
              Settings
            </Typography>
            <Typography variant="body1" sx={{ color: '#5e6d55' }}>
              Manage your account preferences and application settings
            </Typography>
          </Box>

          <Grid container spacing={3}>
            {/* Notifications Settings */}
            <Grid item xs={12}>
              <SettingsCard elevation={0}>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  mb: 2,
                  color: '#108a00'
                }}>
                  <Notifications sx={{ mr: 2 }} />
                  <Typography variant="h6">Notifications</Typography>
                </Box>
                <Divider sx={{ mb: 3 }} />
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <FormControlLabel
                      control={<StyledSwitch defaultChecked />}
                      label={
                        <Box>
                          <Typography variant="subtitle1">Email Notifications</Typography>
                          <Typography variant="body2" color="text.secondary">
                            Receive updates about your account via email
                          </Typography>
                        </Box>
                      }
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormControlLabel
                      control={<StyledSwitch defaultChecked />}
                      label={
                        <Box>
                          <Typography variant="subtitle1">Push Notifications</Typography>
                          <Typography variant="body2" color="text.secondary">
                            Get instant updates in your browser
                          </Typography>
                        </Box>
                      }
                    />
                  </Grid>
                </Grid>
              </SettingsCard>
            </Grid>

            {/* Security Settings */}
            <Grid item xs={12}>
              <SettingsCard elevation={0}>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  mb: 2,
                  color: '#108a00'
                }}>
                  <Security sx={{ mr: 2 }} />
                  <Typography variant="h6">Security</Typography>
                </Box>
                <Divider sx={{ mb: 3 }} />
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <FormControlLabel
                      control={<StyledSwitch defaultChecked />}
                      label={
                        <Box>
                          <Typography variant="subtitle1">Two-Factor Authentication</Typography>
                          <Typography variant="body2" color="text.secondary">
                            Add an extra layer of security to your account
                          </Typography>
                        </Box>
                      }
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormControlLabel
                      control={<StyledSwitch />}
                      label={
                        <Box>
                          <Typography variant="subtitle1">Login Alerts</Typography>
                          <Typography variant="body2" color="text.secondary">
                            Get notified of new login attempts
                          </Typography>
                        </Box>
                      }
                    />
                  </Grid>
                </Grid>
              </SettingsCard>
            </Grid>

            {/* Language & Theme Settings */}
            <Grid item xs={12} md={6}>
              <SettingsCard elevation={0}>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  mb: 2,
                  color: '#108a00'
                }}>
                  <Language sx={{ mr: 2 }} />
                  <Typography variant="h6">Language & Region</Typography>
                </Box>
                <Divider sx={{ mb: 3 }} />
                <FormControlLabel
                  control={<StyledSwitch defaultChecked />}
                  label={
                    <Box>
                      <Typography variant="subtitle1">Use System Language</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Automatically detect and use your system language
                      </Typography>
                    </Box>
                  }
                />
              </SettingsCard>
            </Grid>

            <Grid item xs={12} md={6}>
              <SettingsCard elevation={0}>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  mb: 2,
                  color: '#108a00'
                }}>
                  <ColorLens sx={{ mr: 2 }} />
                  <Typography variant="h6">Appearance</Typography>
                </Box>
                <Divider sx={{ mb: 3 }} />
                <FormControlLabel
                  control={<StyledSwitch />}
                  label={
                    <Box>
                      <Typography variant="subtitle1">Dark Mode</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Switch between light and dark themes
                      </Typography>
                    </Box>
                  }
                />
              </SettingsCard>
            </Grid>

            {/* Save Button */}
            <Grid item xs={12}>
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'flex-end',
                mt: 2 
              }}>
                <Button
                  variant="contained"
                  size="large"
                  sx={{
                    minWidth: 200,
                    backgroundColor: '#108a00',
                    '&:hover': {
                      backgroundColor: '#0c6b00'
                    }
                  }}
                >
                  Save Changes
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </PageWrapper>

      <Footer />
    </Box>
  );
};

export default Settings; 